// The Result Decoder's math: recomputes p from a reported statistic, checks it against the reported p
// the way statcheck does (Nuijten et al., 2016; the statcheck R package 1.5.0), converts the statistic to
// effect sizes, reads the df back as sample sizes, and ranks the tests that could have produced it.
// Distribution functions come from lib/power.js (checked against scipy in spec/javascript/power_check.mjs);
// this file's own checks are spec/javascript/result_check.mjs.
// Text fields use *x* for a math-italic symbol and *x_y* for one with a subscript.
import { tCdf, fSf, chi2Cdf, normalCdf, normalQuantile, nctCdf, ncfCdf } from "./power.js";
import { parseResults } from "./result_parse.js";

export const ALPHA = 0.05;

// ---------- Formatting ----------

const MINUS = "−";
export function fmt(x, dec = 2, leadingZero = true) {
	if (x == null || !Number.isFinite(x)) return "–";
	let s = Math.abs(x).toFixed(dec);
	if (!leadingZero) s = s.replace(/^0\./, ".");
	return (x < 0 && Number(s) !== 0 ? MINUS : "") + s;
}
export function fmtP(p) {
	if (p == null || !Number.isFinite(p)) return "–";
	if (p < 0.001) return "< .001";
	return "= " + fmt(p, 3, false);
}
const fmtN = (n) => (Number.isInteger(n) ? String(n) : fmt(n, 1));
const roundTo = (x, dec) => Math.round(x * Math.pow(10, dec)) / Math.pow(10, dec);
const half = (dec) => 0.5 / Math.pow(10, dec);

// ---------- p-values (statcheck's compute_p) ----------

// Two-tailed by default for t, z and r; F and χ² use their upper tail, which is already non-directional.
export function computeP(type, value, df1, df2, twoTailed = true) {
	if (!Number.isFinite(value)) return null;
	let p;
	switch (type) {
		case "t":
			if (!(df2 > 0)) return null;
			p = tCdf(-Math.abs(value), df2);
			break;
		case "z":
			p = normalCdf(-Math.abs(value));
			break;
		case "r": {
			if (!(df2 > 0) || Math.abs(value) > 1) return null;
			if (Math.abs(value) === 1) return 0;
			const t = value / Math.sqrt((1 - value * value) / df2);
			p = tCdf(-Math.abs(t), df2);
			break;
		}
		case "F":
			if (!(df1 > 0 && df2 > 0) || value < 0) return null;
			p = fSf(value, df1, df2);
			break;
		case "chi2": case "Q": case "H":
			if (!(df1 > 0) || value < 0) return null;
			p = 1 - chi2Cdf(value, df1);
			break;
		default:
			return null;
	}
	if (twoTailed && (type === "t" || type === "z" || type === "r")) p *= 2;
	return Math.min(1, Math.max(0, p));
}

// The interval the statistic could have been rounded from: 2.2 could be anything from 2.15 to 2.25.
function statBounds(value, dec) {
	const h = half(dec);
	return value >= 0 ? [value - h, value + h] : [value + h, value - h];
}

// statcheck's error_test: is the reported p inconsistent with the statistic, allowing for rounding?
// pComp is "=", "<", ">" or "ns"; statComp is "=", "<" or ">".
export function errorTest({ type, value, decimals, df1, df2, statComp = "=", pComp, reportedP, pDecimals, twoTailed = true, alpha = ALPHA }) {
	if (pComp === "ns") { reportedP = alpha; pComp = ">"; }
	const [lowStat, upStat] = statBounds(value, decimals);
	const upP = computeP(type, lowStat, df1, df2, twoTailed);
	const lowP = computeP(type, upStat, df1, df2, twoTailed);
	if (upP == null || lowP == null) return null;
	const dec = pDecimals ?? 3;
	if (statComp === "=") {
		if (pComp === "=") return reportedP > roundTo(upP, dec) || reportedP < roundTo(lowP, dec);
		if (pComp === "<") return reportedP < lowP;
		if (pComp === ">") return reportedP > upP;
	} else if (statComp === "<") {
		if (pComp === "=") return reportedP < roundTo(upP, dec);
		if (pComp === "<") return reportedP < upP;
		if (pComp === ">") return false;
	} else if (statComp === ">") {
		if (pComp === "=") return reportedP > roundTo(lowP, dec);
		if (pComp === "<") return false;
		if (pComp === ">") return reportedP > lowP;
	}
	return null;
}

// statcheck's decision_error_test with its defaults (α = .05, p = α counts as significant).
export function decisionErrorTest({ reportedP, computedP, statComp = "=", pComp, alpha = ALPHA }) {
	if (pComp === "ns") { reportedP = alpha; pComp = ">"; }
	if (statComp === "=") {
		if (pComp === "=") return (reportedP <= alpha && computedP > alpha) || (reportedP > alpha && computedP <= alpha);
		if (pComp === "<") return reportedP <= alpha && computedP > alpha;
		if (pComp === ">") return reportedP >= alpha && computedP <= alpha;
	} else if (statComp === "<") {
		if (pComp === "=" || pComp === "<") return reportedP <= alpha && computedP >= alpha;
		return false;
	} else if (statComp === ">") {
		if (pComp === "=") return reportedP > alpha && computedP <= alpha;
		if (pComp === ">") return reportedP >= alpha && computedP <= alpha;
		return false;
	}
	return false;
}

// The test whose p we can recompute, and its numbers: U and W are checked through their z.
function checkable(result) {
	const s = result.stat;
	if ((s.type === "U" || s.type === "W") && result.z) return { ...result.z, type: "z", via: s.type };
	if (["t", "F", "chi2", "Q", "H", "r", "z"].includes(s.type)) {
		const type = s.type === "chi2" || s.type === "Q" || s.type === "H" ? "chi2" : s.type;
		return { ...s, type };
	}
	return null;
}

const SIDED = new Set(["t", "z", "r"]);

// Values that can't happen: an r above 1, a negative F, a p above 1.
function impossibilities(result) {
	const s = result.stat, out = [];
	if (s.type === "r" && Math.abs(s.value) > 1) out.push("A correlation can't be beyond −1 or 1.");
	if (["F", "chi2", "H", "Q", "U", "W"].includes(s.type) && s.value < 0) out.push(`This statistic can't be negative.`);
	if (s.type === "OR" && s.value <= 0) out.push("An odds ratio must be above 0.");
	if ((s.df1 != null && s.df1 <= 0) || (s.df2 != null && s.df2 <= 0)) out.push("Degrees of freedom must be above 0.");
	if (result.p && result.p.value != null && (result.p.value > 1 || result.p.value < 0)) out.push("A p-value must be between 0 and 1.");
	for (const e of result.effects) {
		if (["eta2p", "eta2", "eta2g", "omega2", "omega2p", "epsilon2", "V", "R2", "adjR2", "kendallW"].includes(e.key) && (e.value > 1 || (e.value < 0 && !["omega2", "omega2p", "adjR2"].includes(e.key))))
			out.push(`${e.name} must be between 0 and 1.`);
		if ((e.key === "r" || e.key === "rs" || e.key === "phi") && Math.abs(e.value) > 1) out.push(`${e.name} can't be beyond −1 or 1.`);
	}
	return out;
}

// Altman & Bland (2011): p from a ratio's 95% CI, on the log scale, assuming a Wald interval.
export function pFromRatioCi(lo, hi, level = 95) {
	if (!(lo > 0 && hi > lo)) return null;
	const zc = normalQuantile(1 - (1 - level / 100) / 2);
	const se = (Math.log(hi) - Math.log(lo)) / (2 * zc);
	const est = (Math.log(hi) + Math.log(lo)) / 2;
	if (!(se > 0)) return null;
	return 2 * normalCdf(-Math.abs(est / se));
}

// The consistency check for one result. Statuses: consistent, one-tailed (consistent only if one-tailed,
// and the paste says one-tailed), inconsistent, decision (a decision error), impossible, unchecked.
export function checkConsistency(result) {
	const notes = [];
	const bad = impossibilities(result);
	if (bad.length) return { status: "impossible", headline: "Impossible Value", message: bad.join(" "), notes };
	const p = result.p;
	const s = result.stat;
	if (s.type === "OR") return checkRatio(result);
	if (s.type === "fit") return { status: "unchecked", headline: "No χ² to Check", message: "Only fit indices were found, with no model χ² and df, so there's no p-value to check.", notes };
	const c = checkable(result);
	if (!c) {
		const why = s.type === "U" ? "The p-value for U depends on the two group sizes, which the result doesn't give. Report z with it and the decoder can check that."
			: s.type === "W" ? "The p-value for a signed-rank statistic depends on the number of pairs, which the result doesn't give. Report z with it and the decoder can check that."
			: s.type === "W01" ? "Shapiro–Wilk and Kendall's W need the sample size and their own tables, so p can't be recomputed from W alone."
			: "This statistic can't be turned into a p-value on its own.";
		return { status: "unchecked", headline: "Not Checkable", message: why, notes };
	}
	const needsDf = c.type === "t" || c.type === "r" ? c.df2 == null : c.type === "F" ? c.df1 == null || c.df2 == null : c.type === "chi2" ? c.df1 == null : false;
	if (needsDf) return { status: "unchecked", headline: "Not Checkable", message: "No degrees of freedom were given, so p can't be recomputed.", notes };
	const computedP = computeP(c.type, c.value, c.df1, c.df2, true);
	if (computedP == null) return { status: "unchecked", headline: "Not Checkable", message: "These numbers don't give a p-value.", notes };
	const base = { computedP, via: c.via, notes };
	if (c.via) notes.push(`Checked through *z* = ${fmt(c.value, c.decimals)}, the normal approximation reported with ${c.via === "U" ? "*U*" : "the signed-rank statistic"}.`);
	if (s.type === "H") notes.push("Kruskal–Wallis *H* is read against χ², an approximation; software may report an exact p for small samples.");
	if (!p) return { ...base, status: "unchecked", headline: "No p-Value to Check", message: `No p-value was reported with this result. Recomputed, *p* ${fmtP(computedP)} (two-tailed${SIDED.has(c.type) ? "" : " by nature"}).` };
	if (p.adjusted) return { ...base, status: "unchecked", headline: "Adjusted p", message: `This p was adjusted for multiple comparisons, so it should be larger than the unadjusted *p* ${fmtP(computedP)}. It can't be checked without knowing the adjustment.` };

	let pComp = p.comp, reportedP = p.value, pDecimals = p.decimals;
	if (pComp === "=" && reportedP === 0) {
		pComp = "<"; reportedP = 0.001; pDecimals = 3;
		notes.push("*p* = .000 is how software prints a p below .0005. A p-value is never exactly zero, so it's read here as *p* < .001, which is how APA style reports it. statcheck, by default, counts *p* = .000 as an error.");
	}
	if (pComp !== "=" && pComp !== "ns" && reportedP === 0) return { ...base, status: "impossible", headline: "Impossible Value", message: "No p-value can be below 0." };

	const args = { type: c.type, value: c.value, decimals: c.decimals, df1: c.df1, df2: c.df2, statComp: c.comp, pComp, reportedP, pDecimals };
	const [lowStat, upStat] = statBounds(c.value, c.decimals);
	const range = [computeP(c.type, upStat, c.df1, c.df2), computeP(c.type, lowStat, c.df1, c.df2)];
	const error = errorTest(args);
	if (error == null) return { ...base, status: "unchecked", headline: "Not Checkable", message: "These numbers couldn't be compared." };
	const reportedText = pComp === "ns" ? "ns" : `*p* ${pComp} ${fmt(reportedP, Math.max(pDecimals ?? 3, 2), false)}`;
	const recomputedText = `*p* ${fmtP(computedP)}`;
	const fitTest = s.type === "chi2" && isModelFit(result);
	if (fitTest) notes.push("SEM software often reports a scaled or robust χ² (such as Satorra–Bentler's), whose p can differ slightly from the plain χ² distribution's.");
	// Truncated rather than rounded: .306 printed as .30. statcheck still counts it, but it's worth saying.
	let truncated = false;
	if (error && pComp === "=" && pDecimals != null) {
		const k = Math.pow(10, pDecimals);
		const trunc = (x) => Math.floor(x * k + 1e-9) / k;
		const hit = [computedP, ...range].find((x) => x != null && Math.abs(trunc(x) - reportedP) < 1e-9 && Math.abs(roundTo(x, pDecimals) - reportedP) <= 1 / k + 1e-9);
		if (hit != null) truncated = true;
		if (hit != null) notes.unshift(`The authors may have truncated rather than rounded: ${fmt(hit, pDecimals + 1, false)} truncates to ${fmt(reportedP, pDecimals, false)}.`);
	}
	const roundingText = c.comp === "=" && range[0] != null && Math.abs(range[1] - range[0]) > 0.0005 && computedP >= 0.001
		? ` (anything from ${fmt(range[0], 3, false)} to ${fmt(range[1], 3, false)} once the statistic's rounding is allowed for)` : "";
	if (!error) {
		return { ...base, range, status: "consistent", headline: "Consistent",
			message: `Recomputed from the statistic and df, ${recomputedText}${SIDED.has(c.type) ? ", two-tailed" : ""}${roundingText}. That fits the reported ${reportedText}.` };
	}
	// One-tailed? Only meaningful for t, z and r.
	let oneTailedFits = false, oneTailedP = null;
	if (SIDED.has(c.type)) {
		oneTailedP = computeP(c.type, c.value, c.df1, c.df2, false);
		oneTailedFits = errorTest({ ...args, twoTailed: false }) === false;
	}
	if (oneTailedFits && result.oneTailed) {
		return { ...base, range, oneTailedP, status: "one-tailed", headline: "Consistent (One-Tailed)",
			message: `The paste says the test was one-tailed, and the one-tailed *p* ${fmtP(oneTailedP)} fits the reported ${reportedText}. (Two-tailed it would be ${recomputedText}.)` };
	}
	const decision = decisionErrorTest({ reportedP, computedP, statComp: c.comp, pComp, alpha: ALPHA });
	const reportedSig = pComp === "ns" ? false : pComp === ">" ? false : reportedP <= ALPHA;
	const oneTailedNote = oneTailedFits ? ` It would fit if the test were one-tailed (*p* ${fmtP(oneTailedP)}), but nothing in the paste says so.` : "";
	if (decision) {
		return { ...base, range, oneTailedP, oneTailedFits, status: "decision", headline: "Decision Error",
			message: reportedSig
				? `Reported as significant (${reportedText}), but recomputed from the statistic and df, ${recomputedText}${roundingText}: not significant at .05.${oneTailedNote}`
				: `Reported as not significant (${reportedText}), but recomputed from the statistic and df, ${recomputedText}${roundingText}: significant at .05.${oneTailedNote}`,
			after: "The mismatch changes the conclusion. Any of the numbers could hold the typo: the statistic, the df or the p-value." };
	}
	return { ...base, range, oneTailedP, oneTailedFits, status: "inconsistent", headline: "Minor Inconsistency",
		message: `Recomputed from the statistic and df, ${recomputedText}${roundingText}, not the reported ${reportedText}. A small mismatch, not a decision error: the conclusion at .05 stays the same.${oneTailedNote}`,
		after: truncated ? null : "Any of the numbers could hold the typo: the statistic, the df or the p-value." };
}

// ---------- Model fit (CFA, SEM, path models) ----------

const FIT_WORDS = /\b(?:models?|fit|CFA|SEM|path|measurement model|structural)\b/i;
const NOT_SEM = /logistic|regression|likelihood|predictor|omnibus|Nagelkerke|deviance|goodness[\s-]of[\s-]fit/i;

// A χ² reported with fit indices, or in a sentence about a model's fit, tests the model's fit.
export function isModelFit(result) {
	if (!result || !result.stat) return false;
	if (result.stat.type === "fit") return true;
	if (result.stat.type !== "chi2") return false;
	if (result.fits && result.fits.length) return true;
	const context = result.context || "";
	return FIT_WORDS.test(context) && !NOT_SEM.test(context);
}

// RMSEA from χ², df and N. lavaan (by default) and Mplus divide by N ("normal" likelihood); EQS, LISREL and
// lavaan with likelihood = "wishart" divide by N − 1 (lavaan's lavOptions documentation).
export const rmseaFrom = (chi2, df, n) => (df > 0 && n > 0 ? Math.sqrt(Math.max(0, chi2 - df) / (df * n)) : null);

const overlaps = (a, b) => a[0] <= b[1] + 1e-12 && b[0] <= a[1] + 1e-12;
const span = (x, dec) => [x - half(dec), x + half(dec)];

const FIT_TEXT = {
	chi2df: "The normed (or relative) chi-square: χ² divided by its df, a rough way to allow for the model's size. Cutoffs for it vary widely (2, 3 and 5 have all been suggested), and it inherits χ²'s dependence on N, so it's a weak guide on its own.",
	cfi: "How much better the model fits than a baseline model in which the measured variables are unrelated, from 0 to 1. Higher is better.",
	tli: "Like CFI, it compares the model with the baseline model, but it penalizes complexity and can fall outside 0 to 1. Higher is better.",
	rmsea: "How much the model misfits per degree of freedom, adjusted for sample size. 0 means no more misfit than chance would produce; smaller is better.",
	srmr: "The typical gap between the observed correlations and the ones the model implies. Smaller is better.",
	gfi: "The share of the observed variances and covariances the model reproduces. It rises with sample size and is less used now.",
	agfi: "GFI adjusted for the number of parameters. Like GFI, it rises with sample size and is less used now.",
	aic: "An information criterion: only meaningful when compared across models fit to the same data, where lower is better. The value alone says nothing about fit.",
	bic: "An information criterion that penalizes complexity more than AIC: only meaningful when compared across models fit to the same data, where lower is better."
};

// Each fit index read back, labelled with the glossary's benchmarks, and checked where the χ² allows.
export function fitChecks(result, terms = {}) {
	const s = result.stat;
	const haveChi = s && s.type === "chi2" && s.comp === "=" && s.df1 > 0;
	const chiRange = haveChi ? span(s.value, s.decimals) : null;
	const N = result.N;
	return (result.fits || []).map((f) => {
		const out = { ...f, text: FIT_TEXT[f.key] || "", band: f.term ? benchmarkBand(terms[f.term], f.value) : null, check: null };
		const rep = span(f.value, f.decimals);
		if (f.key === "chi2df" && haveChi) {
			const v = s.value / s.df1;
			const ok = overlaps(rep, [chiRange[0] / s.df1, chiRange[1] / s.df1]);
			out.check = { status: ok ? "match" : "differs", message: ok
				? `Matches χ² ÷ df = ${fmt(s.value, s.decimals)} ÷ ${fmtN(s.df1)} = ${fmt(v, 3)}, allowing for rounding.`
				: `χ² ÷ df = ${fmt(s.value, s.decimals)} ÷ ${fmtN(s.df1)} = ${fmt(v, 3)}, not ${fmt(f.value, f.decimals)}. One of the numbers may hold a typo.` };
		}
		if (f.key === "cfi" || f.key === "tli") out.check = { status: "info", message: `${f.label} can't be recomputed here: it needs the baseline model's χ² and df, which papers rarely report.` };
		if (f.key === "srmr") out.check = { status: "info", message: "SRMR comes from the residual correlations, so it can't be recomputed from χ²." };
		if (f.key === "rmsea") out.check = rmseaCheck(f, s, chiRange, N, haveChi);
		if (f.key === "rmsea" && f.ci) {
			const lvl = f.ci.level || 90;
			out.ciText = `${lvl}% CI [${fmt(f.ci.lo, f.ci.loDecimals, false)}, ${fmt(f.ci.hi, f.ci.hiDecimals, false)}]${f.ci.level ? "" : " (90% is usual for RMSEA)"}`;
			if (f.value < f.ci.lo - 1e-12 || f.value > f.ci.hi + 1e-12) out.ciNote = "The RMSEA lies outside its own interval, which can't happen: check for a typo.";
			else if (f.ci.hi <= 0.05) out.ciNote = "The whole interval sits at or below .05, the conventional line for close fit.";
			else if (f.ci.lo > 0.05) out.ciNote = "Even the interval's lower end is above .05, so close fit is unlikely.";
		}
		return out;
	});
}

function rmseaCheck(f, s, chiRange, N, haveChi) {
	if (!haveChi) return null;
	const df = s.df1;
	const rep = span(f.value, f.decimals);
	if (N > 1) {
		const byN = [rmseaFrom(chiRange[0], df, N), rmseaFrom(chiRange[1], df, N)];
		const byN1 = [rmseaFrom(chiRange[0], df, N - 1), rmseaFrom(chiRange[1], df, N - 1)];
		const a = overlaps(rep, byN), b = overlaps(rep, byN1);
		const formula = `√(max(0, χ² − df) ÷ (df × *N*)) = ${fmt(rmseaFrom(s.value, df, N), 3, false)} with *N* = ${N}`;
		if (a && b) return { status: "match", message: `Matches ${formula}. Dividing by *N* (lavaan's default, and Mplus) or by *N* − 1 (EQS, LISREL) gives the same value at this rounding.` };
		if (a) return { status: "match", message: `Matches ${formula}, dividing by *N* as lavaan (by default) and Mplus do.` };
		if (b) return { status: "match", message: `Matches √(max(0, χ² − df) ÷ (df × (*N* − 1))) = ${fmt(rmseaFrom(s.value, df, N - 1), 3, false)}, dividing by *N* − 1 as EQS and LISREL do (and lavaan with likelihood = "wishart").` };
		return { status: "differs", message: `Recomputed from χ², df and *N* = ${N}: ${fmt(rmseaFrom(s.value, df, N), 3, false)} (dividing by *N*) or ${fmt(rmseaFrom(s.value, df, N - 1), 3, false)} (by *N* − 1), not ${fmt(f.value, f.decimals, false)}. A robust or scaled RMSEA, or several groups (multiplied by √*G*), can explain a gap; so can a typo.` };
	}
	if (chiRange[1] <= df) {
		return f.value - half(f.decimals) <= 1e-12
			? { status: "match", message: "χ² is below its df, so RMSEA is 0 at any sample size: consistent." }
			: { status: "differs", message: `χ² is below its df, so RMSEA should be 0 at any sample size, not ${fmt(f.value, f.decimals, false)}.` };
	}
	if (f.value <= 0) return { status: "info", message: "No *N* was given, so RMSEA can't be checked." };
	// What N does the reported RMSEA imply? N = (χ² − df) ÷ (df × RMSEA²), give or take 1 for the N − 1 convention.
	const excess = Math.max(0, s.value - df);
	const hiR = rep[1], loR = rep[0];
	const nLow = excess / (df * hiR * hiR);
	const nHigh = loR > 0 ? excess / (df * loR * loR) : Infinity;
	const round = (n) => (n >= 1000 ? Math.round(n / 100) * 100 : n >= 100 ? Math.round(n / 10) * 10 : Math.round(n));
	if (!(nLow >= 2)) return { status: "info", message: "No *N* was given, so RMSEA can't be checked." };
	return { status: "info", message: Number.isFinite(nHigh) && nHigh / nLow < 20
		? `No *N* was given. With this χ² and df, an RMSEA of ${fmt(f.value, f.decimals, false)} implies a sample of roughly ${round(nLow)} to ${round(nHigh)} (the rounding of the RMSEA leaves that much room). If the paper's *N* is far outside that range, something's off.`
		: `No *N* was given. With this χ² and df, an RMSEA of ${fmt(f.value, f.decimals, false)} implies a sample of at least ${round(nLow)} or so.` };
}

// OR with a 95% CI and p: the CI implies an approximate p (Altman & Bland, 2011), and whether it excludes 1.
function checkRatio(result) {
	const notes = [];
	const ci = result.effects.find((e) => e.ci)?.ci || result.cis[0];
	const p = result.p;
	if (!ci) return { status: "unchecked", headline: "Not Checkable", message: "An odds ratio's p can be checked against its confidence interval, but no interval was reported.", notes };
	if (!p) return { status: "unchecked", headline: "No p-Value to Check", message: `No p-value was reported. The interval implies roughly *p* ${fmtP(pFromRatioCi(ci.lo, ci.hi, ci.level))}.`, notes };
	if (ci.level !== 95) return { status: "unchecked", headline: "Not Checkable", message: `Only a 95% interval lines up with p < .05; this one is ${ci.level}%.`, notes };
	// Every rounding corner of the interval.
	const ps = [];
	for (const lo of [ci.lo - half(ci.loDecimals), ci.lo + half(ci.loDecimals)]) for (const hi of [ci.hi - half(ci.hiDecimals), ci.hi + half(ci.hiDecimals)]) {
		const v = pFromRatioCi(lo, hi, 95);
		if (v != null) ps.push(v);
	}
	if (!ps.length) return { status: "unchecked", headline: "Not Checkable", message: "The interval's numbers can't be used.", notes };
	const minP = Math.min(...ps), maxP = Math.max(...ps), midP = pFromRatioCi(ci.lo, ci.hi, 95);
	notes.push("Approximate: assumes a Wald interval, symmetric on the log scale (Altman & Bland, 2011). Likelihood-based or exact intervals can differ a little.");
	const excludes = ci.lo > 1 || ci.hi < 1;
	const pComp = p.comp === "=" && p.value === 0 ? "<" : p.comp;
	const reportedP = p.comp === "=" && p.value === 0 ? 0.001 : p.value;
	const reportedSig = pComp === "ns" || pComp === ">" ? false : reportedP <= ALPHA;
	const reportedText = pComp === "ns" ? "ns" : `*p* ${pComp} ${fmt(reportedP, Math.max(p.decimals ?? 3, 2), false)}`;
	if (excludes !== reportedSig && !(pComp === "<" && reportedP > ALPHA)) {
		return { status: "decision", computedP: midP, headline: "Decision Error", notes,
			message: excludes ? `The 95% CI [${fmt(ci.lo)}, ${fmt(ci.hi)}] leaves out 1, which means significant at .05, but the result is reported as ${reportedText}.`
				: `The 95% CI [${fmt(ci.lo)}, ${fmt(ci.hi)}] includes 1, which means not significant at .05, but the result is reported as ${reportedText}.`,
			after: "One of the interval, the odds ratio or the p-value probably holds a typo." };
	}
	const dec = p.decimals ?? 3;
	let off;
	if (pComp === "=") off = reportedP > roundTo(maxP, dec) + half(dec) * 2 || reportedP < roundTo(minP, dec) - half(dec) * 2;
	else if (pComp === "<") off = reportedP < minP;
	else off = reportedP > maxP;
	return off
		? { status: "inconsistent", computedP: midP, headline: "Doesn't Match", notes, message: `The interval implies roughly *p* ${fmtP(midP)}, not the reported ${reportedText}. The conclusion at .05 stays the same.` }
		: { status: "consistent", computedP: midP, headline: "Consistent", notes, message: `The interval implies roughly *p* ${fmtP(midP)}, which fits the reported ${reportedText}.` };
}

// ---------- Effect sizes ----------

// Noncentrality bounds for a t: δ with P(T ≤ t | δ) = .975 and .025 (as effectsize's .get_ncp_t).
export function ncpTInterval(t, df, conf = 0.95) {
	if (!(df > 0) || !Number.isFinite(t)) return null;
	const a = (1 - conf) / 2;
	const width = 6 * Math.max(1, Math.sqrt(1 + (t * t) / (2 * df)));
	const lo0 = t - width, hi0 = t + width;
	if (Math.abs(lo0) > 37 || Math.abs(hi0) > 37) return null; // AS 243 underflows beyond this
	const solve = (target) => {
		let lo = lo0, hi = hi0;
		for (let i = 0; i < 80; i++) {
			const mid = (lo + hi) / 2;
			if (nctCdf(t, df, mid) > target) lo = mid; else hi = mid;
		}
		return (lo + hi) / 2;
	};
	return [solve(1 - a), solve(a)];
}

// Noncentrality bounds for an F: λ with P(F ≤ f | λ) = .975 and .025, floored at 0 (as effectsize's .get_ncp_F).
export function ncpFInterval(f, df1, df2, conf = 0.95) {
	if (!(df1 > 0 && df2 > 0 && f >= 0)) return null;
	const a = (1 - conf) / 2;
	const at0 = ncfCdf(f, df1, df2, 0);
	const solve = (target) => {
		if (at0 <= target) return 0;
		let hi = Math.max(10, 2 * f * df1);
		while (ncfCdf(f, df1, df2, hi) > target) {
			hi *= 2;
			if (hi > 1400) return null; // the Poisson weights underflow past here
		}
		let lo = 0;
		for (let i = 0; i < 80; i++) {
			const mid = (lo + hi) / 2;
			if (ncfCdf(f, df1, df2, mid) > target) lo = mid; else hi = mid;
		}
		return (lo + hi) / 2;
	};
	const lo = solve(1 - a), hi = solve(a);
	return lo == null || hi == null ? null : [lo, hi];
}

export const conversions = {
	// Design-free.
	rFromT: (t, df) => t / Math.sqrt(t * t + df),
	eta2pFromF: (F, df1, df2) => (F * df1) / (F * df1 + df2),
	f2FromF: (F, df1, df2) => (F * df1) / df2,
	wFromChi2: (chi2, N) => Math.sqrt(chi2 / N),
	rFromZ: (z, N) => z / Math.sqrt(N),
	// Design-dependent.
	dIndependent: (t, df) => (2 * t) / Math.sqrt(df), // two independent groups of equal size (effectsize::t_to_d)
	dOneSample: (t, n) => t / Math.sqrt(n), // one-sample d, or paired d_z, with n people or pairs
	vFromChi2: (chi2, N, m) => Math.sqrt(chi2 / (N * m)), // m = min(rows, columns) − 1
	epsilon2FromH: (H, N) => H / ((N * N - 1) / (N + 1)), // rank ε² (effectsize::rank_epsilon_squared)
	kendallWFromChi2: (chi2, N, k) => chi2 / (N * (k - 1)) // Friedman's χ² with N people and k conditions
};

export function rInterval(t, df, conf = 0.95) {
	const ncp = ncpTInterval(t, df, conf);
	return ncp && ncp.map((d) => d / Math.sqrt(d * d + df));
}
export function eta2pInterval(F, df1, df2, conf = 0.95) {
	const ncp = ncpFInterval(F, df1, df2, conf);
	return ncp && ncp.map((l) => l / (l + df2));
}
export function dInterval(t, df, scale, conf = 0.95) {
	const ncp = ncpTInterval(t, df, conf);
	return ncp && ncp.map((d) => d * scale);
}
// Fisher z interval for a reported r with N pairs.
export function fisherInterval(r, N, conf = 0.95) {
	if (!(N > 3) || Math.abs(r) >= 1) return null;
	const z = Math.atanh(r), se = 1 / Math.sqrt(N - 3), q = normalQuantile(1 - (1 - conf) / 2);
	return [Math.tanh(z - q * se), Math.tanh(z + q * se)];
}

// Contingency table shapes with (rows − 1)(columns − 1) = df, rows ≤ columns.
export function tableShapes(df, max = 12) {
	if (!Number.isInteger(df) || df < 1) return [];
	const out = [];
	for (let r = 2; r <= max + 1; r++) {
		if (df % (r - 1)) continue;
		const c = df / (r - 1) + 1;
		if (c >= r) out.push([r, c]);
	}
	return out;
}

// Effect sizes computable from the statistic. Each: { key (matches reported keys), symbol, name, term, value,
// ci, assumption (null when design-free), range (from the statistic's rounding) }.
export function effectSizes(result) {
	const s = result.stat, out = [];
	if (!s || s.comp !== "=") return out;
	const [a, b] = statBounds(s.value, s.decimals);
	const span = (fn) => { const x = fn(a), y = fn(b); return [Math.min(x, y), Math.max(x, y)]; };
	const N = result.N;
	const intDf = (d) => d != null && Number.isInteger(d);
	if (s.type === "t" && s.df2 > 0) {
		const df = s.df2;
		out.push({ key: "r", symbol: "r", name: "correlation", term: "pearson-r", value: conversions.rFromT(s.value, df), ci: rInterval(s.value, df), assumption: null,
			range: span((t) => conversions.rFromT(t, df)), how: "*r* = *t* ÷ √(*t*² + df). For two groups, this is the point-biserial correlation between group and score." });
		if (intDf(df)) {
			const n = df + 1;
			out.push({ key: "d", symbol: "d_z", name: "Cohen's *d*, one-sample or paired", term: "cohens-d", value: conversions.dOneSample(s.value, n), ci: dInterval(s.value, df, 1 / Math.sqrt(n)),
				assumption: `one sample, or ${n} pairs (paired: this is *d_z*, the mean difference in SDs of the differences)`, range: span((t) => conversions.dOneSample(t, n)), how: `*d* = *t* ÷ √*n*, with *n* = ${n}.` });
		}
		out.push({ key: "d", symbol: "d", name: "Cohen's *d*, independent groups", term: "cohens-d", value: conversions.dIndependent(s.value, df), ci: dInterval(s.value, df, 2 / Math.sqrt(df)),
			assumption: `two independent groups of equal size${intDf(df) ? "" : " (approximate with Welch's df)"}`, range: span((t) => conversions.dIndependent(t, df)), how: "*d* = 2*t* ÷ √df. Unequal groups make the true *d* larger." });
	}
	if (s.type === "F" && s.df1 > 0 && s.df2 > 0) {
		const { df1, df2 } = s;
		out.push({ key: "eta2p", symbol: "η²_p", name: "partial η²", term: "partial-eta-squared", value: conversions.eta2pFromF(s.value, df1, df2), ci: eta2pInterval(s.value, df1, df2), assumption: null,
			range: span((F) => conversions.eta2pFromF(F, df1, df2)), how: "η²*_p* = *F* × df₁ ÷ (*F* × df₁ + df₂). In a one-way between-subjects ANOVA this is also η²." });
		if (/R2|regress|predict|model/i.test(result.context || "") || result.effects.some((e) => e.key === "R2" || e.key === "f2"))
			out.push({ key: "f2", symbol: "f²", name: "Cohen's f²", term: "cohens-f-squared", value: conversions.f2FromF(s.value, df1, df2), ci: null, assumption: null,
				range: span((F) => conversions.f2FromF(F, df1, df2)), how: "*f*² = *F* × df₁ ÷ df₂: the same information as partial η², in regression's units." });
		if (df1 === 1) {
			const t = Math.sqrt(Math.max(0, s.value));
			out.push({ key: "d", symbol: "d", name: "Cohen's |*d*|, two independent groups", term: "cohens-d", value: conversions.dIndependent(t, df2), ci: null,
				assumption: "two independent groups of equal size, with no other factors (F with 1 df is t²; the sign is lost)", range: span((F) => conversions.dIndependent(Math.sqrt(Math.max(0, F)), df2)), how: "|*d*| = 2√*F* ÷ √df₂." });
		}
	}
	// A model-fit χ² measures misfit, not an effect: no w, φ or V.
	if ((s.type === "chi2" || s.type === "Q") && s.df1 > 0 && !isModelFit(result)) {
		if (N) {
			out.push({ key: "w", symbol: "w", name: "Cohen's w", term: "cohens-w", value: conversions.wFromChi2(s.value, N), ci: null, assumption: null,
				range: span((x) => conversions.wFromChi2(x, N)), how: `*w* = √(χ² ÷ *N*), with *N* = ${N}.` });
			if (s.df1 === 1) {
				out.push({ key: "phi", symbol: "φ", name: "|φ|", term: "phi-coefficient", value: conversions.wFromChi2(s.value, N), ci: null,
					assumption: "a 2 × 2 table (the sign depends on how the categories are coded)", range: span((x) => conversions.wFromChi2(x, N)), how: "|φ| = √(χ² ÷ *N*)." });
				out.push({ key: "V", symbol: "V", name: "Cramér's V", term: "cramers-v", value: conversions.vFromChi2(s.value, N, 1), ci: null,
					assumption: "a 2 × 2 table, where V = |φ|", range: span((x) => conversions.vFromChi2(x, N, 1)), how: "*V* = √(χ² ÷ *N*)." });
			} else if (Number.isInteger(s.df1)) {
				const ms = [...new Set(tableShapes(s.df1).map(([r]) => r - 1))];
				for (const m of ms) {
					const shapes = tableShapes(s.df1).filter(([r]) => r - 1 === m).map(([r, c]) => `${r} × ${c}`).join(" or ");
					out.push({ key: "V", symbol: "V", name: "Cramér's V", term: "cramers-v", value: conversions.vFromChi2(s.value, N, m), ci: null,
						assumption: `a ${shapes} table`, range: span((x) => conversions.vFromChi2(x, N, m)), how: `*V* = √(χ² ÷ (*N* × ${m})), since the smaller side has ${m + 1} categories.` });
				}
			}
			if (Number.isInteger(s.df1) && s.df1 >= 1 && /Friedman|Kendall|rank/i.test(result.context || "")) {
				const k = s.df1 + 1;
				out.push({ key: "kendallW", symbol: "W", name: "Kendall's W", term: "kendalls-w", value: conversions.kendallWFromChi2(s.value, N, k), ci: null,
					assumption: `a Friedman test with ${N} people ranking ${k} conditions`, range: span((x) => conversions.kendallWFromChi2(x, N, k)), how: `*W* = χ² ÷ (*N*(*k* − 1)), with *k* = ${k}.` });
			}
		}
	}
	if (s.type === "H" && N > 1) {
		out.push({ key: "epsilon2", symbol: "ε²", name: "rank ε²", term: "epsilon-squared", value: conversions.epsilon2FromH(s.value, N), ci: null, assumption: null,
			range: span((x) => conversions.epsilon2FromH(x, N)), how: `ε² = *H* ÷ ((*N*² − 1) ÷ (*N* + 1)), with *N* = ${N}.` });
	}
	const z = s.type === "z" ? s : result.z;
	if (z && z.comp === "=" && N > 0) {
		const [za, zb] = statBounds(z.value, z.decimals);
		out.push({ key: "r", symbol: "r", name: "correlation", term: s.type === "U" || s.type === "W" ? "rank-biserial-correlation" : "pearson-r", value: conversions.rFromZ(z.value, N), ci: null,
			assumption: `*N* = ${N} is the number of observations behind *z*`, range: [Math.min(za, zb) / Math.sqrt(N), Math.max(za, zb) / Math.sqrt(N)], how: "*r* = *z* ÷ √*N* (Rosenthal's conversion, common after rank tests)." });
	}
	if (s.type === "r" && s.df2 > 0 && Math.abs(s.value) < 1) {
		out.push({ key: s.spearman ? "rs" : "r", symbol: s.spearman ? "r_s" : "r", name: s.spearman ? "Spearman's rho" : "r", term: s.spearman ? "spearman-rho" : "pearson-r", value: s.value,
			ci: fisherInterval(s.value, s.df2 + 2), assumption: null, reported: true, how: `The correlation is its own effect size. Interval by Fisher's *z*, with *N* = ${s.df2 + 2}.` });
	}
	if (s.type === "OR") {
		const ci = result.effects.find((e) => e.ci)?.ci || result.cis[0] || null;
		out.push({ key: "OR", symbol: "OR", name: "odds ratio", term: "odds-ratio", value: s.value, ci: ci ? [ci.lo, ci.hi] : null, ciReported: !!ci, assumption: null, reported: true, how: "The odds ratio is its own effect size." });
	}
	return out;
}

// Does a reported effect size match one computed from the statistic, allowing for both roundings?
const FAMILY = { d: ["d"], dz: ["d"], g: ["d"], r: ["r"], rs: ["r"], eta2p: ["eta2p"], eta2: ["eta2p"], V: ["V", "phi"], phi: ["phi", "V"], w: ["w"], epsilon2: ["epsilon2"], kendallW: ["kendallW"], f2: ["f2"] };

export function compareReported(reported, computed, df) {
	const fam = FAMILY[reported.key];
	if (!fam) return null;
	const cands = computed.filter((c) => fam.includes(c.key) && c.range && !c.reported);
	if (!cands.length) return null;
	const h = half(reported.decimals);
	const rv = reported.value;
	const fits = (c, v) => {
		let [lo, hi] = c.range;
		if (reported.key === "g" && df > 1) lo *= 1 - 3 / (4 * df - 1); // Hedges' correction shrinks d a little
		return v + h >= lo - 1e-12 && v - h <= hi + 1e-12;
	};
	const matches = cands.filter((c) => fits(c, rv));
	const signFlip = !matches.length && cands.filter((c) => fits(c, -rv));
	if (matches.length) return { status: "match", matches, message: `Matches the ${matches.map((m) => m.assumption ? `${m.name}, assuming ${m.assumption}` : m.name).join("; or the ")}.` };
	if (signFlip && signFlip.length) return { status: "sign", matches: signFlip, message: "Matches in size but with the opposite sign. Papers often report the size alone; check which way the difference ran." };
	const nearest = cands.reduce((best, c) => (Math.abs(c.value - rv) < Math.abs(best.value - rv) ? c : best), cands[0]);
	let why = "It may come from a different formula (a pooled or average SD, unequal groups, a correction) or hold a typo.";
	if (reported.key === "eta2" && rv < nearest.value) why = "In a factorial or repeated-measures design, plain η² is smaller than partial η², so this can be right.";
	if ((reported.key === "d" || reported.key === "g") && Math.abs(rv) > Math.abs(nearest.value) && cands.some((c) => c.assumption?.startsWith("two independent")))
		why = "Unequal group sizes make *d* larger than the equal-groups conversion, and repeated-measures designs often use a different SD, so this can be right.";
	return { status: "differs", matches: [], nearest, message: `Doesn't match any conversion from the statistic (closest: ${nearest.name} = ${fmt(nearest.value, 2, !bounded(nearest.key))}). ${why}` };
}

const BOUNDED = new Set(["r", "rs", "eta2p", "eta2", "eta2g", "V", "phi", "epsilon2", "kendallW", "R2", "adjR2", "omega2", "omega2p", "tau"]);
export const bounded = (key) => BOUNDED.has(key);

// ---------- Benchmarks ----------

// The site's band for a value: the last band whose threshold it reaches. Signed terms (range labels with |x|)
// use the size; odds ratios below 1 use the reciprocal.
export function benchmarkBand(term, value) {
	if (!term || !term.benchmarks || !term.benchmarks.length || value == null || !Number.isFinite(value)) return null;
	let x = value;
	if (term.slug === "odds-ratio") { if (x <= 0) return null; x = x < 1 ? 1 / x : x; }
	else if (term.benchmarks.some((b) => /\|/.test(b.range)) || value < 0 && term.benchmarks.every((b) => b.threshold == null || b.threshold >= 0)) x = Math.abs(x);
	// A band written with a strict lower bound (".05 < RMSEA ≤ .08", "SRMR > .08") starts just above its threshold.
	const strict = (b) => /^[\s(|]*[-−]?[.\d]+\s*<(?!=)/.test(b.range || "") || /[^<≥]>\s*[-−]?[.\d]+\s*$/.test(b.range || "");
	let band = null;
	for (const b of term.benchmarks) if (b.threshold == null || (strict(b) ? x > b.threshold + 1e-12 : x >= b.threshold - 1e-12)) band = b;
	return band || term.benchmarks[0];
}

// The p-value ladder: from the recomputed p when we have one, else from what was reported.
export function evidence(result, check, pTerm) {
	if (!pTerm) return null;
	let p = null, basis = null;
	// An odds ratio's p from its interval is only approximate, so the ladder uses the reported p there.
	if (check && check.computedP != null && result.stat.type !== "OR" && ["consistent", "inconsistent", "decision", "unchecked"].includes(check.status)) { p = check.computedP; basis = "recomputed"; }
	else if (check && check.status === "one-tailed") { p = check.oneTailedP; basis = "one-tailed"; }
	else if (result.p && result.p.comp === "=" && result.p.value > 0) { p = result.p.value; basis = "reported"; }
	else if (result.p && result.p.comp === "<" && result.p.value <= 0.001) { p = result.p.value / 2; basis = "bound"; }
	else if (result.p && (result.p.comp === "=" && result.p.value === 0)) { p = 0.0001; basis = "bound"; }
	if (p == null) {
		if (result.p && (result.p.comp === "<")) {
			const bands = pTerm.benchmarks.filter((b) => b.threshold == null || b.threshold < result.p.value);
			return { band: null, range: bands, basis: "inequality", p: null };
		}
		return null;
	}
	const band = benchmarkBand(pTerm, p);
	return { band, basis, p, fitMeaning: isModelFit(result) ? fitEvidence(band) : null };
}

// For a model-fit χ², small p is evidence against the model, not for an effect: the same ladder, reworded.
function fitEvidence(band) {
	if (!band) return null;
	const t = band.threshold ?? 0;
	if (t >= 0.1) return "Little evidence that the model misfits: the data are consistent with the model reproducing them exactly. That doesn't prove the model right; other models may fit as well.";
	if (t >= 0.05) return "Weak evidence that the model misfits; not significant at .05.";
	if (t >= 0.01) return "Significant misfit at .05: moderate evidence that the model doesn't reproduce the data exactly. With a large sample, even trivial misfit reaches this; read the fit indices too.";
	if (t >= 0.001) return "Strong evidence that the model doesn't reproduce the data exactly. With a large sample, even small misfit gets here; read the fit indices for how much.";
	return "Very strong evidence that the model doesn't reproduce the data exactly. With a large sample, even small misfit gets here; read the fit indices for how much.";
}

// ---------- Reading the numbers back ----------

const signWord = (v) => (v < 0 ? "below" : "above");

function dfReading(s, modelFit = false) {
	const rows = [];
	if (s.type === "t" && s.df2 != null) {
		const df = s.df2;
		if (Number.isInteger(df)) {
			rows.push({ label: `(${s.dfText})`, term: "degrees-of-freedom", text: `${df} degrees of freedom. What that says about the sample depends on the design:`,
				list: [`One-sample or paired: *n* = ${df + 1} people (or pairs), since df = *n* − 1.`,
					`Independent samples (Student's): *N* = ${df + 2} across the two groups, since df = *N* − 2.`,
					`A correlation's test, or the slope in simple regression: *N* = ${df + 2}.`,
					`A coefficient in multiple regression with *k* predictors: *N* = ${df} + *k* + 1.`] });
		} else {
			rows.push({ label: `(${s.dfText})`, term: "degrees-of-freedom", text: `Fractional df usually mean Welch's *t*-test, which doesn't assume equal variances, or a mixed model's approximation (Satterthwaite or Kenward–Roger). The sample size can't be read off exactly; for Welch's test, *N* is at least ${Math.ceil(df + 2)}.` });
		}
	}
	if (s.type === "F" && s.df1 != null && s.df2 != null) {
		const { df1, df2 } = s;
		const intBoth = Number.isInteger(df1) && Number.isInteger(df2);
		if (intBoth) {
			const shapes = tableShapes(df1, 6).filter(([r, c]) => r > 1 && c > 1).map(([r, c]) => `${r} × ${c}`);
			rows.push({ label: "df₁", value: fmtN(df1), term: "degrees-of-freedom", text: `${df1} df for the effect${df1 === 1 ? " (a comparison of two means, or one predictor: *F* with 1 df is *t*²)" : ""}:`,
				list: [`One-way ANOVA: *k* = ${df1 + 1} groups (or conditions), since df₁ = *k* − 1.`,
					`A factorial main effect: a factor with ${df1 + 1} levels. An interaction: (*a* − 1)(*b* − 1) = ${df1}${shapes.length ? `, as in a ${shapes.join(" or ")} design` : ""}.`,
					`A regression's overall *F*: ${df1} predictor${df1 === 1 ? "" : "s"}.`] });
			const rm = (df2 / df1) + 1;
			rows.push({ label: "df₂", value: fmtN(df2), term: "degrees-of-freedom", text: `${df2} df for error:`,
				list: [`One-way between-subjects ANOVA, or the regression: *N* = ${df1 + df2 + 1} (df₁ + df₂ + 1).`,
					`ANCOVA with *c* covariates: *N* = ${df1 + df2 + 1} + *c*. A factorial design: *N* = ${df2} + the number of cells.`,
					Number.isInteger(rm) && df1 > 0 ? `One-way repeated measures: df₂ = (*k* − 1)(*n* − 1), so *n* = ${rm} people, each measured ${df1 + 1} times.` : `One-way repeated measures would need df₂ to be a multiple of df₁ (${df2} ÷ ${df1} isn't whole), so it's probably not that design.`] });
		} else {
			rows.push({ label: `(${s.dfText})`, term: "degrees-of-freedom", text: !Number.isInteger(df1)
				? "Fractional df on both sides mean a sphericity correction (Greenhouse–Geisser or Huynh–Feldt), which multiplies both df by ε: a repeated-measures design. The uncorrected df, and so the sample size, can't be read off exactly."
				: `A fractional error df with a whole df₁ usually means Welch's ANOVA (unequal variances; *k* = ${df1 + 1} groups) or a mixed model's approximation. The sample size can't be read off exactly.` });
		}
	}
	if (s.type === "chi2" && s.df1 != null && modelFit) {
		rows.push({ label: `(${s.dfText})`, term: "degrees-of-freedom", text: `The model's ${fmtN(s.df1)} degrees of freedom: the number of distinct variances and covariances (and means, if modeled) the data supply, minus the number of parameters the model estimates. More df means a more constrained model, which has more ways to misfit.` });
		return rows;
	}
	if ((s.type === "chi2" || s.type === "Q" || s.type === "H") && s.df1 != null) {
		const df = s.df1;
		const list = [];
		if (s.type === "chi2") {
			const shapes = tableShapes(df).map(([r, c]) => `${r} × ${c}`);
			if (shapes.length) list.push(`A test of association in a contingency table: (rows − 1)(columns − 1) = ${df}, so a ${shapes.slice(0, 4).join(", ")}${shapes.length > 4 ? " (or larger)" : ""} table.`);
			list.push(`Goodness of fit: ${df + 1} categories, since df = *k* − 1.`);
			if (df === 1) list.push("McNemar's test of paired yes/no answers always has 1 df.");
			list.push(`Friedman or Kruskal–Wallis reported as χ², or Cochran's *Q*: ${df + 1} conditions or groups.`);
			list.push(`A likelihood-ratio (model) test: the model being tested has ${df} more parameter${df === 1 ? "" : "s"}. A model-fit χ² in factor analysis or SEM: the model's df${df > 10 ? ", which fits a df this large" : ""}.`);
		} else if (s.type === "H") list.push(`Kruskal–Wallis: *k* = ${df + 1} groups, since df = *k* − 1.`);
		else list.push(`Cochran's *Q*: ${df + 1} related conditions. A meta-analysis's heterogeneity *Q*: ${df + 1} studies.`);
		rows.push({ label: `(${s.dfText})`, term: "degrees-of-freedom", text: `${fmtN(df)} degrees of freedom:`, list });
	}
	if (s.type === "r" && s.df2 != null) {
		rows.push({ label: `(${s.dfText})`, term: "degrees-of-freedom", text: Number.isInteger(s.df2) ? `${s.df2} degrees of freedom, so *N* = ${s.df2 + 2} pairs of scores (df = *N* − 2).` : "Fractional df: an unusual report for a correlation; check the source." });
	}
	return rows;
}

const STAT_INFO = {
	t: { symbol: "t", name: "*t* statistic", term: "t-statistic" },
	F: { symbol: "F", name: "*F* statistic", term: "f-statistic" },
	chi2: { symbol: "χ²", name: "chi-square statistic", term: "chi-square-statistic" },
	Q: { symbol: "Q", name: "*Q* statistic", term: "chi-square-statistic" },
	H: { symbol: "H", name: "Kruskal–Wallis *H*", term: "kruskal-wallis-h" },
	r: { symbol: "r", name: "correlation", term: "pearson-r" },
	z: { symbol: "z", name: "*z* statistic", term: "z-statistic" },
	U: { symbol: "U", name: "Mann–Whitney *U*", term: "mann-whitney-u" },
	W: { symbol: "W", name: "signed-rank statistic", term: "wilcoxon-signed-rank-statistic" },
	W01: { symbol: "W", name: "*W* between 0 and 1", term: "shapiro-wilk-w" },
	OR: { symbol: "OR", name: "odds ratio", term: "odds-ratio" },
	fit: { symbol: "", name: "set of model-fit indices", term: "model-fit" }
};

function statReading(result) {
	const s = result.stat;
	const v = s.value, abs = fmt(Math.abs(v), s.decimals);
	const bound = s.comp !== "=" ? ` Reported as a bound (${s.comp} ${fmt(v, s.decimals)}), not an exact value.` : "";
	const sym = s.type === "chi2" ? (s.symbol === "G2" ? "G²" : "χ²") : s.type === "r" && s.spearman ? "r_s" : (STAT_INFO[s.type]?.symbol || s.symbol);
	const info = { ...STAT_INFO[s.type], symbol: sym };
	if (s.type === "chi2" && s.symbol === "G2") info.name = "likelihood-ratio G²";
	if (s.type === "r" && s.spearman) { info.name = "Spearman's rho"; info.term = "spearman-rho"; }
	if (s.type === "Q" && /w|W|between|within|b|B/.test(s.symbol.slice(1))) info.name = "heterogeneity *Q*";
	const modelFit = isModelFit(result);
	if (s.type === "chi2" && modelFit) { info.name = "model-fit chi-square"; info.term = "model-fit"; }
	info.showSymbol = !info.name.includes("*") && s.type !== "fit";
	let text = "";
	if (s.type === "chi2" && modelFit) {
		text = "How far the variances and covariances the model implies are from the ones observed. The null hypothesis is that the model reproduces the data exactly, so here a non-significant p is the good news: no significant misfit. With a large sample, even trivial misfit comes out significant, which is why fit indices are reported alongside it.";
		return { info, text: text + bound };
	}
	if (s.type === "fit") return { info, text: "Fit indices with no model χ² next to them, so there's no p-value to check. Each index is read below." };
	switch (s.type) {
		case "t": text = `How far the estimate is from what the null hypothesis predicts (usually no difference), in standard errors: ${abs} standard errors ${signWord(v)} it.`; break;
		case "F": text = `How much variation the effect explains compared with what chance alone would produce. ${v < 1 ? "Below 1: less than chance alone would give." : `Here ${abs} times as much; near 1 would mean no effect.`}`; break;
		case "chi2": text = s.symbol === "G2" ? "How far the observed counts are from those the model expects, on the likelihood-ratio scale. Read against χ² with the same df." : "How far the observed counts are from those expected if the null hypothesis were true, added up over the cells. Larger means further."; break;
		case "Q": text = "Read against χ² with the same df. For Cochran's Q: how unevenly the yes answers spread across related conditions. For a meta-analysis: how much the studies' results vary beyond chance."; break;
		case "H": text = "How far apart the groups' average ranks are. Read against χ² with *k* − 1 df."; break;
		case "r": text = `How closely the two variables move together, from −1 to 1. ${v > 0 ? "Positive: higher on one goes with higher on the other." : v < 0 ? "Negative: higher on one goes with lower on the other." : "Zero: no linear relationship."}`; break;
		case "z": text = `How far the estimate is from the null value, in standard errors (${abs}, ${signWord(v)} it), read against the normal distribution. No df needed.`; break;
		case "U": text = `Out of every pair that takes one score from each group, how many the first group wins (ties count half). With no difference, *U* is about *n*₁*n*₂ ÷ 2. Some software reports the smaller of the two U values.`; break;
		case "W": text = `The sum of the ranks of the positive (or negative, or the smaller) differences. Note: R's wilcox.test also calls the Mann–Whitney statistic *W*.`; break;
		case "W01": text = "A W between 0 and 1 is most likely Shapiro–Wilk's test of normality (closer to 1 means closer to a normal shape) or Kendall's W, the agreement among rankings (0 none, 1 perfect)."; break;
		case "OR": text = v > 1 ? `The odds of the outcome are ${fmt(v)} times as high in one group (or per unit of the predictor) as in the other.` : v < 1 ? `The odds of the outcome are ${fmt(v)} times as high, that is ${fmt((1 - v) * 100, 0)}% lower, in one group (or per unit of the predictor) than the other.` : "Equal odds: no association."; break;
		default: text = "";
	}
	return { info, text: text + bound };
}

function direction(result) {
	const s = result.stat;
	const v = s.type === "U" || s.type === "W" ? result.z?.value : s.value;
	if (s.type === "fit") return null;
	if (s.type === "chi2" && isModelFit(result)) return "χ² has no sign. Here it measures misfit: larger means the model reproduces the data less well.";
	if (["F", "chi2", "Q", "H"].includes(s.type)) return `${s.type === "F" ? "*F*" : s.type === "H" ? "*H*" : s.type === "Q" ? "*Q*" : "χ²"} has no sign: it says ${s.type === "chi2" || s.type === "Q" ? "the counts depart from the null hypothesis" : "the groups differ somewhere"}, not which way. Look at the means, proportions or follow-up tests for that.`;
	if (s.type === "OR") return v > 1 ? "Above 1: higher odds in the group (or direction) the authors coded as the comparison." : v < 1 ? "Below 1: lower odds in the group (or direction) the authors coded as the comparison." : "Exactly 1: no difference in odds.";
	if (s.type === "W01") return null;
	if (v == null) return null;
	if (s.type === "r") return v > 0 ? "Positive: the two go up together." : v < 0 ? "Negative: as one goes up, the other goes down." : "Zero: no linear relationship.";
	const which = v < 0 ? "lower" : "higher";
	return v === 0 ? "Zero: no difference at all." : `${v < 0 ? "Negative" : "Positive"}: the first group or measurement (or the sample, against its comparison value) came out ${which}. Which one counts as "first" is the authors' choice, so check the means to see which way the difference ran.`;
}

function pReading(p, modelFit = false) {
	if (!p) return null;
	// For a model-fit χ², the null is the model, not "no effect".
	const ifNull = modelFit ? "if the model reproduced the data exactly, misfit at least this large" : "if there were truly no effect, a result at least this extreme";
	if (p.comp === "ns") return { label: "ns", text: modelFit ? "Not significant: no significant misfit. The authors didn't give the exact p-value." : "Not significant. The authors didn't give the exact p-value, only that it's above the usual .05." };
	const v = p.value;
	if (p.comp === "=" && v === 0) return { label: "p = .000", text: "Software prints .000 when p rounds to zero at three decimals. A p-value is never exactly zero: report it as *p* < .001." };
	const lead = p.leadingZero ? " APA style drops the leading zero, since p can't exceed 1." : "";
	if (p.comp === "<") return { label: `p < ${fmt(v, Math.max(p.decimals, 2), false)}`, text: `Below ${fmt(v, Math.max(p.decimals, 2), false)}: ${ifNull} would turn up less than ${pct(v)} of the time. The exact value wasn't given.${lead}` };
	if (p.comp === ">") return { label: `p > ${fmt(v, Math.max(p.decimals, 2), false)}`, text: `Above ${fmt(v, Math.max(p.decimals, 2), false)}: the authors give only a bound.${lead}` };
	const sentence = ifNull.charAt(0).toUpperCase() + ifNull.slice(1);
	return { label: `p = ${fmt(v, Math.max(p.decimals, 2), false)}`, text: `${sentence} would turn up about ${pct(v)} of the time.${modelFit && v > ALPHA ? " Not significant: no significant misfit." : ""}${lead}${p.adjusted ? " This p was adjusted for multiple comparisons." : ""}` };
}
const pct = (v) => `${(v * 100).toFixed(v < 0.01 ? 2 : v < 0.1 ? 1 : 0)}%`;

// ---------- Which test? ----------

// Structural candidates per statistic: slugs are looked up in the site's published tests (passed in), so a
// slug that isn't published is shown without a link. `cue` is matched against the paste around the result.
// Where a model-fit χ² and fit indices come from. `fit` marks them, so they rise when the χ² is a fit test.
const FIT_CANDIDATES = [
	{ slug: "confirmatory-factor-analysis", name: "Confirmatory Factor Analysis (CFA)", base: 0.4, fit: true, cue: /CFA|factor|measurement model|loading|indicator|item/i,
		why: () => "Testing whether items load on the factors they're meant to measure." },
	{ slug: "path-analysis", name: "Path Analysis", base: 0.3, fit: true, cue: /path|structural|mediat|direct|indirect|\bSEM\b/i,
		why: () => "A structural model of how measured variables affect one another." },
	{ slug: "growth-curve-modeling", name: "Growth-Curve Modeling", base: 0.2, fit: true, cue: /growth|latent curve|trajector|slope|intercept|wave|over time/i,
		why: () => "Change over time modeled as latent intercepts and slopes." },
	{ slug: "multilevel-sem", name: "Multilevel SEM", base: 0.1, fit: true, cue: /multilevel|two-level|cluster|nested|between-level|within-level/i,
		why: () => "A structural model with people nested in groups." }
];

const CANDIDATES = {
	t: [
		{ slug: "independent-samples-t-test", name: "Independent-Samples t-Test", base: 3, cue: /group|condition|control|treatment|versus|vs\.?|between|welch|men|women|male|female/i,
			why: (s) => (Number.isInteger(s.df2) ? `Two separate groups, *N* = ${s.df2 + 2} in total.` : "Fractional df suggest Welch's version, for unequal variances.") },
		{ slug: "paired-samples-t-test", name: "Paired-Samples t-Test", base: 2.5, cue: /paired|pre-?test|post-?test|before|after|baseline|follow-?up|within|repeated|time 1|\bDZ\b|matched|twin/i, intOnly: true,
			why: (s) => `The same ${s.df2 + 1} people measured twice, or ${s.df2 + 1} matched pairs.` },
		{ slug: "one-sample-t-test", name: "One-Sample t-Test", base: 2, cue: /one-?sample|compared (?:with|to) (?:a|the)|test value|chance|recommended|norm|midpoint|population mean|μ/i, intOnly: true,
			why: (s) => `One sample of ${s.df2 + 1} compared with a fixed value.` },
		{ slug: "pearson-correlation", name: "Pearson Correlation (the test of r)", base: 1, cue: /correlat|associat|relationship|related|\br\s?=|\bRS\b/i, intOnly: true,
			why: (s) => `Testing whether a correlation differs from zero, *N* = ${s.df2 + 2}.` },
		{ slug: "multiple-linear-regression", alt: ["simple-linear-regression"], name: "A Regression Coefficient", base: 1, cue: /\bb\s?=|\bB\s?=|β|beta|regress|predict|coefficient|slope|SE\s?=/i,
			why: (s) => `Testing one predictor's coefficient: *N* = df + *k* + 1 with *k* predictors (${Number.isInteger(s.df2) ? s.df2 + 2 : "df + 2"} with one).` },
		{ slug: "multilevel-regression", name: "Multilevel Regression (HLM)", base: 0.2, cue: /multilevel|mixed|random|HLM|cluster|nested|Satterthwaite|Kenward|lme4|lmer/i, fractionalBonus: 1.5,
			why: () => "A fixed effect in a mixed model, whose df are often approximated and fractional." }
	],
	F: [
		{ slug: "one-way-between-subjects-anova", name: "One-Way Between-Subjects ANOVA", base: 3, cue: /group|condition|one-?way|between|welch/i,
			why: (s) => (Number.isInteger(s.df2) ? `*k* = ${s.df1 + 1} groups, *N* = ${s.df1 + s.df2 + 1}.` : `*k* = ${s.df1 + 1} groups; a fractional error df suggests Welch's ANOVA.`) },
		{ slug: "factorial-between-subjects-anova", name: "Factorial Between-Subjects ANOVA", base: 2, cue: /interaction|main effect|factorial|two-?way|three-?way|\d\s?[x×]\s?\d|×/i,
			why: (s) => `One effect in a design with two or more factors: a factor with ${s.df1 + 1} levels, or an interaction with ${s.df1} df.` },
		{ slug: "one-way-repeated-measures-anova", name: "One-Way Repeated-Measures ANOVA", base: 1.5, cue: /repeated|within|\btimes?\b|session|trial|sphericity|Greenhouse|Huynh|\bGG\b|ε|epsilon|wave/i, fractionalBonus: 2.5,
			why: (s) => (Number.isInteger(s.df1) && Number.isInteger(s.df2) && Number.isInteger(s.df2 / s.df1) ? `${s.df1 + 1} conditions; df₂ = (*k* − 1)(*n* − 1) gives *n* = ${s.df2 / s.df1 + 1} people.` : "Conditions measured on the same people; fractional df mean a sphericity correction.") },
		{ slug: "mixed-factorial-anova", name: "Mixed Factorial ANOVA", base: 0.8, cue: /mixed|within-between|between-within|group\s?[x×]\s?time|interaction/i,
			why: () => "One between-groups factor crossed with a within-person factor, such as group × time." },
		{ slug: "multiple-linear-regression", alt: ["simple-linear-regression"], name: "A Regression's Overall F", base: 1.5, cue: /\bR2\b|regress|predict|model/i,
			why: (s) => `${s.df1} predictor${s.df1 === 1 ? "" : "s"}, *N* = ${Number.isInteger(s.df2) ? s.df1 + s.df2 + 1 : "df₁ + df₂ + 1"}.` },
		{ slug: "hierarchical-regression", name: "Hierarchical Regression (F Change)", base: 0.5, cue: /DR2|change|step|block|hierarch|added/i,
			why: (s) => `Adding ${s.df1} predictor${s.df1 === 1 ? "" : "s"} to a model.` },
		{ slug: "ancova", name: "ANCOVA", base: 1, cue: /covari|controll|adjust|ANCOVA|baseline/i,
			why: (s) => `Groups compared after adjusting for covariates: *N* = ${Number.isInteger(s.df2) ? s.df1 + s.df2 + 1 : "df₁ + df₂ + 1"} + the number of covariates.` },
		{ slug: "manova", name: "MANOVA", base: 0.2, cue: /Pillai|Wilks|Λ|MANOVA|multivariate|Hotelling|\bRoy/i,
			why: () => "An approximate F from Pillai's trace or Wilks' Λ, across several outcomes at once." }
	],
	chi2: [
		{ slug: "pearson-chi-square-test", name: "Pearson Chi-Square Test of Association", base: 3, cue: /associat|independen|contingency|table|proportion|percent|%|cross/i,
			why: (s) => { const sh = tableShapes(s.df1).map(([r, c]) => `${r} × ${c}`); return sh.length ? `A ${sh.slice(0, 3).join(" or ")} table of counts.` : "A table of counts."; } },
		{ slug: null, name: "Chi-Square Goodness of Fit", base: 1.5, cue: /goodness|expected|equal(?:ly)? (?:likely|distributed)|uniform/i,
			why: (s) => `Counts in ${s.df1 + 1} categories compared with expected proportions.` },
		{ slug: "mcnemars-test", name: "McNemar's Test", base: 1, onlyDf: 1, cue: /McNemar|paired|before|after|change|pre|post/i,
			why: () => "Paired yes/no answers, such as before and after." },
		{ slug: "logistic-regression", name: "A Likelihood-Ratio (Model) Test", base: 1, cue: /likelihood|\bLR\b|model|logistic|deviance|Nagelkerke|Cox|predictor|Wald|omnibus|block/i,
			why: (s) => `Comparing models that differ by ${s.df1} parameter${s.df1 === 1 ? "" : "s"}.` },
		{ slug: "friedman-test", name: "Friedman Test", base: 0.6, cue: /Friedman|rank|conditions|Kendall/i,
			why: (s) => `${s.df1 + 1} related conditions, ranked within each person.` },
		{ slug: "kruskal-wallis-test", name: "Kruskal–Wallis Test (as χ²)", base: 0.5, cue: /Kruskal|rank|H\s?=/i,
			why: (s) => `${s.df1 + 1} independent groups, compared on ranks.` },
		{ slug: "cochrans-q", name: "Cochran's Q", base: 0.4, cue: /Cochran|yes\/no|binary|dichotom/i,
			why: (s) => `${s.df1 + 1} related yes/no conditions.` },
		...FIT_CANDIDATES
	],
	fit: FIT_CANDIDATES,
	Q: [
		{ slug: "cochrans-q", name: "Cochran's Q", base: 2, cue: /Cochran|yes\/no|binary|dichotom|condition/i, why: (s) => `${s.df1 + 1} related yes/no conditions.` },
		{ slug: null, name: "A Meta-Analysis's Heterogeneity Q", base: 1, cue: /heterogen|meta|studies|I2|between-stud/i, why: (s) => `${s.df1 + 1} studies.` }
	],
	H: [{ slug: "kruskal-wallis-test", name: "Kruskal–Wallis Test", base: 3, why: (s) => (s.df1 != null ? `${s.df1 + 1} independent groups, compared on ranks.` : "Independent groups compared on ranks.") }],
	r: [
		{ slug: "pearson-correlation", name: "Pearson Correlation", base: 3, cue: /Pearson|linear/i, notSpearman: true, why: (s) => (s.df2 != null ? `*N* = ${s.df2 + 2} pairs of scores.` : "Two measured variables.") },
		{ slug: "spearman-correlation", name: "Spearman Correlation", base: 1, cue: /Spearman|rank|\bRS\b|ordinal|monoton/i, spearmanBonus: 4, why: () => "Correlating the ranks rather than the raw scores." },
		{ slug: "convergent-validity", name: "Convergent Validity", base: 0.3, cue: /validity|convergent|scale|measure|questionnaire/i, why: () => "Two measures of the same construct, correlated." },
		{ slug: null, name: "Point-Biserial Correlation", base: 0.3, cue: /point-?biserial|dichotom|group/i, why: () => "A correlation with a two-category variable: the same test as an independent-samples t-test." }
	],
	z: [
		{ slug: "z-test-for-proportions", name: "z-Test for Proportions", base: 1.5, cue: /proportion|percent|%|\brates?\b/i, why: () => "Comparing two proportions." },
		{ slug: "logistic-regression", name: "A Wald z in Logistic (or Other) Regression", base: 1.5, cueCase: /\bOR\b/, cue: /odds|logistic|\bb\s?=|\bB\s?=|coefficient|Wald|predict|SE\s?=/i, why: () => "A coefficient divided by its standard error." },
		{ slug: "mann-whitney-u-test", name: "Mann–Whitney U Test (normal approximation)", base: 1, cue: /Mann|Whitney|rank|U\s?=/i, why: () => "Two independent groups compared on ranks." },
		{ slug: "wilcoxon-signed-rank-test", name: "Wilcoxon Signed-Rank Test (normal approximation)", base: 1, cue: /Wilcoxon|signed|paired|rank|pre|post/i, why: () => "Paired scores compared on ranks." },
		{ slug: "comparing-correlation-coefficients", name: "Comparing Correlation Coefficients", base: 0.5, cue: /Fisher|compar.{0,30}correlat|correlations? differ|difference between.{0,30}correlat/i, why: () => "Two correlations compared through Fisher's z." },
		{ slug: "mediation-analysis", name: "Mediation (Sobel Test)", base: 0.3, cue: /indirect|Sobel|mediat/i, why: () => "An indirect effect divided by its standard error." },
		{ slug: "cohens-kappa", name: "Cohen's Kappa (its z test)", base: 0.2, cue: /kappa|κ|agreement|rater/i, why: () => "Testing whether agreement beats chance." }
	],
	U: [{ slug: "mann-whitney-u-test", name: "Mann–Whitney U Test", base: 3, why: () => "Two independent groups compared on ranks." }],
	W: [
		{ slug: "wilcoxon-signed-rank-test", name: "Wilcoxon Signed-Rank Test", base: 3, why: () => "Paired scores compared on the ranks of their differences." },
		{ slug: "mann-whitney-u-test", name: "Mann–Whitney U Test (as R reports it)", base: 1, cue: /group|independent|rank-?sum|Mann|Whitney|wilcox\.test/i, why: () => "R's wilcox.test prints the Mann–Whitney statistic as W." }
	],
	W01: [
		{ slug: "distribution-fitting", name: "Shapiro–Wilk Test (Distribution Fitting)", base: 2, cue: /normal|Shapiro|Wilk|distribution/i, why: () => "A test of whether scores look normally distributed." },
		{ slug: "friedman-test", name: "Kendall's W (with the Friedman Test)", base: 1, cue: /Kendall|agreement|concordance|Friedman|rank/i, why: () => "Agreement among rankings, from 0 to 1." }
	],
	OR: [
		{ slug: "logistic-regression", name: "Logistic Regression", base: 3, cue: /logistic|predict|adjust|controll|aOR|covariate|per\b|unit/i, why: () => "Odds of a yes/no outcome per unit of a predictor." },
		{ slug: "fishers-exact-test", name: "Fisher's Exact Test", base: 1, cue: /Fisher|exact|2\s?[x×]\s?2|small/i, why: () => "Odds compared in a 2 × 2 table." },
		{ slug: "mcnemars-test", name: "McNemar's Test", base: 0.5, cue: /McNemar|paired|before|after/i, why: () => "Odds of changing one way versus the other." },
		{ slug: "multilevel-logistic-regression", name: "Multilevel Logistic Regression", base: 0.3, cue: /multilevel|mixed|random|cluster|nested|glmer/i, why: () => "Odds with clustered data." },
		{ slug: "ordinal-regression", name: "Ordinal Regression", base: 0.3, cue: /ordinal|proportional odds|Likert|ordered/i, why: () => "Odds of a higher category on an ordered outcome." },
		{ slug: "generalized-estimating-equations", name: "Generalized Estimating Equations (GEE)", base: 0.2, cue: /GEE|estimating equations|population-averaged/i, why: () => "Population-averaged odds with repeated or clustered data." }
	]
};

// Which glossary terms each statistic is reported under, for the "other tests on this site" list.
const STAT_TERMS = { t: ["t-statistic"], F: ["f-statistic", "f-change"], chi2: ["chi-square-statistic", "likelihood-ratio-test"], Q: ["chi-square-statistic"],
	H: ["kruskal-wallis-h"], z: ["z-statistic"], U: ["mann-whitney-u"], W: ["wilcoxon-signed-rank-statistic"], W01: ["shapiro-wilk-w", "kendalls-w"], r: ["pearson-r", "spearman-rho"], OR: ["odds-ratio"] };

export function rankTests(result, tests = []) {
	const s = result.stat;
	const bySlug = new Map(tests.map((t) => [t.slug, t]));
	const context = result.context || "";
	const fractional = (s.df1 != null && !Number.isInteger(s.df1)) || (s.df2 != null && !Number.isInteger(s.df2));
	// A model-fit χ² puts the SEM family first; with fit indices printed beside it, the rest drop out.
	const modelFit = isModelFit(result);
	const hasFits = s.type === "fit" || (result.fits && result.fits.length > 0);
	const list = (CANDIDATES[s.type] || []).filter((c) => !(c.intOnly && fractional) && !(c.onlyDf && s.df1 !== c.onlyDf) && !(hasFits && !c.fit) && !(c.fit && !modelFit && !(s.df1 > 10))).map((c) => {
		let score = c.base;
		const worded = !!((c.cue && c.cue.test(context)) || (c.cueCase && c.cueCase.test(context)));
		if (worded) score += 3;
		if (modelFit) score += c.fit ? 3 : -4;
		else if (c.fit && s.df1 > 10) score += 1.5;
		if (fractional && c.fractionalBonus) score += c.fractionalBonus;
		if (c.bigDfBonus && s.df1 > 10) score += c.bigDfBonus;
		if (c.spearmanBonus && s.spearman) score += c.spearmanBonus;
		if (c.notSpearman && s.spearman) score -= 3;
		if (s.type === "z" && result.via === "U" && c.slug === "mann-whitney-u-test") score += 5;
		const site = [c.slug, ...(c.alt || [])].map((slug) => slug && bySlug.get(slug)).find(Boolean) || null;
		let why = "";
		try { why = c.why(s); } catch { why = ""; }
		const structural = (fractional && c.fractionalBonus) || (c.bigDfBonus && s.df1 > 10) || (c.spearmanBonus && s.spearman);
		const specific = worded || !!structural;
		return { name: c.name, slug: site?.slug || null, path: site?.path || null, siteName: site?.name || null, why, score, specific, cued: specific || (modelFit && !!c.fit) };
	}).sort((a, b) => b.score - a.score);
	// "Most likely" needs a reason in the paste (a word, or the df's shape); without one the order is how common each test is.
	const top = list.length ? list[0].score : 0;
	for (const c of list) {
		c.likelihood = list.length === 1 ? "The usual source"
			: c.specific && c.score >= top - 0.01 ? "Most likely"
			: c.score >= 2.5 ? (c.cued ? "Likely" : "Common")
			: c.score >= 1.5 ? "Possible" : "Less likely";
	}
	const linked = new Set(list.map((c) => c.slug).filter(Boolean));
	const termSlugs = modelFit ? ["cfi", "rmsea", "srmr"] : STAT_TERMS[s.type] || [];
	const others = tests.filter((t) => !linked.has(t.slug) && (t.statistics || []).some((x) => termSlugs.includes(x))).map((t) => ({ name: t.name, path: t.path, slug: t.slug }));
	return { ranked: list, others };
}

// ---------- Everything for one paste ----------

export function decode(text, site = {}) {
	const terms = site.terms || {};
	let parsed;
	try { parsed = parseResults(text); } catch { parsed = { results: [], unread: [{ text: String(text).slice(0, 200) }], orphans: [] }; }
	const results = parsed.results.map((r) => {
		try {
			return decodeOne(r, site, terms);
		} catch (e) {
			return { id: r.id, raw: r.raw, failed: true };
		}
	});
	return { results, unread: parsed.unread || [], orphans: parsed.orphans || [], empty: !String(text || "").trim() };
}

function decodeOne(r, site, terms) {
	const { info, text } = statReading(r);
	const s = r.stat;
	const modelFit = isModelFit(r);
	const pieces = s.type === "fit" ? [] : [{ label: s.type === "W01" ? "W" : info.symbol, value: `${s.comp} ${fmt(s.value, s.decimals, s.type !== "r")}`, term: info.term, name: info.name, text }];
	pieces.push(...dfReading(s, modelFit));
	if (r.N && !(s.type === "chi2" || s.type === "H" || s.type === "Q") ) pieces.push({ label: "N", value: `= ${r.N}`, term: "sample-size", text: "The total sample size, as reported." });
	else if (r.N) pieces.push({ label: "N", value: `= ${r.N}`, term: "sample-size", text: `The total sample size: ${r.N} ${s.type === "chi2" && !modelFit ? "people or observations counted in the table" : "people or observations"}.` });
	if (r.z) pieces.push({ label: "z", value: `= ${fmt(r.z.value, r.z.decimals)}`, term: "z-statistic", text: `The normal approximation to *${s.type === "U" ? "U" : "W"}*: ${fmt(Math.abs(r.z.value), r.z.decimals)} standard errors ${signWord(r.z.value)} what no difference would give.` });
	const pr = pReading(r.p, modelFit);
	if (pr) pieces.push({ label: "p", value: pr.label.replace(/^p /, ""), term: "p-value", text: pr.text });
	for (const ep of r.extraP) pieces.push({ label: "p", value: `${ep.comp} ${fmt(ep.value, Math.max(ep.decimals || 2, 2), false)}`, term: "p-value", text: "A second p-value; only the first is checked." });
	const check = checkConsistency({ ...r, via: r.z && (s.type === "U" || s.type === "W") ? s.type : null });
	const computed = effectSizes(r);
	const df = s.df2 ?? s.df1;
	const reported = r.effects.map((e) => {
		const comparison = compareReported(e, computed, df);
		return { ...e, band: benchmarkBand(terms[e.term], e.value), comparison };
	});
	for (const c of computed) c.band = benchmarkBand(terms[c.term], c.value);
	for (const ci of r.cis) pieces.push({ label: `${ci.level}% CI`, value: `[${fmt(ci.lo)}, ${fmt(ci.hi)}]`, term: "confidence-interval", text: `The range of values the data are reasonably consistent with, for the estimate this result reports${ci.assumed ? " (taken as 95%; the paste doesn't say)" : ""}.` });
	const ev = evidence(r, check, terms["p-value"]);
	return { id: r.id, raw: r.raw, type: s.type, info, pieces, direction: direction(r), check, computed, reported, evidence: ev, modelFit, fits: fitChecks(r, terms),
		tests: rankTests({ ...r, via: check.via }, site.tests || []), others: r.others };
}
